import { Suspense, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { useGameStore } from '@hollowveil/shared';
import { GameScene } from './scenes/GameScene';
import { GameUI } from './components/ui/GameUI';
import { LoadingScreen } from './components/ui/LoadingScreen';
import { TitleScreen } from './components/ui/TitleScreen';
import { FPSCounter } from './components/ui/FPSCounter';
import { FullscreenButton } from './components/ui/FullscreenButton';
import { OrientationOverlay } from './components/ui/OrientationOverlay';
import { useVisualEffectFilter, useVisualEffectOverlay } from './components/effects/PostEffects';
import { useInputHandler } from './hooks/useInputHandler';
import { defaultAdventure } from './data/adventures';

// Wrapper component that applies CSS filter
function CanvasWrapper() {
  const filter = useVisualEffectFilter();

  return (
    <Canvas
      shadows
      style={{
        width: '100%',
        height: '100%',
        filter: filter || undefined,
        transition: 'filter 0.3s ease-in-out',
      }}
      gl={{
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance',
      }}
      dpr={[1, 2]}
    >
      <Suspense fallback={null}>
        <GameScene />
      </Suspense>
    </Canvas>
  );
}

// Effect overlay component for advanced visual effects
function EffectOverlay() {
  const overlay = useVisualEffectOverlay();

  if (!overlay) {
    return null;
  }

  return <div className={`effect-overlay ${overlay}`} />;
}

export function App() {
  const { loadConfig, isLoaded, showTitleScreen } = useGameStore();

  useEffect(() => {
    // Load the default adventure configuration (Le (R)éveil de l'Éducatrice)
    // This can be overridden by the adventure selector in the title screen
    loadConfig(defaultAdventure.config);
    // Don't auto-load progress - let user choose from title screen
  }, [loadConfig]);

  // Initialize input handlers
  useInputHandler();

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  return (
    <div className="game-container">
      <CanvasWrapper />
      <EffectOverlay />
      <GameUI />
      <FPSCounter />
      <FullscreenButton />
      <OrientationOverlay />
      {showTitleScreen && <TitleScreen />}
    </div>
  );
}
